import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  MagnifyingGlass, CaretRight, CaretDown, Crown, Buildings, User as UserIcon,
  Trash, Prohibit, CheckCircle, Warning
} from "@phosphor-icons/react";
import { AdminShell } from "@/components/AdminShell";
import api, { errDetail } from "@/lib/api";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const cost4 = (v) => `$${Number(v || 0).toFixed(4)}`;

const CodeBadge = ({ row, testId }) => row.code ? (
  <span data-testid={testId} title={`code ${row.code} — pays $${Number(row.revenue || 0).toFixed(2)} instead of $${Number(row.full_price || 0).toFixed(2)}/mo`}
    className={`text-[10px] font-bold rounded-full px-2 py-0.5 whitespace-nowrap ${row.discount_pct >= 100 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
    🎟 {row.discount_pct >= 100 ? "FREE" : `−${row.discount_pct}%`} · {row.code}
  </span>
) : null;

const Pl = ({ value }) => (
  <span className={`font-semibold ${value >= 0 ? "text-emerald-600" : "text-red-600"}`}>
    {value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(2)}
  </span>
);

const EconChips = ({ row, testId }) => (
  <div data-testid={testId} className="flex items-center gap-3 text-xs text-gray-500">
    <span>Revenue <b className="text-gray-800">{money(row.revenue)}</b></span>
    <span>Tokens <b className="text-gray-800">{Number(row.tokens || 0).toLocaleString()}</b></span>
    <span>My cost <b className="text-gray-800">{cost4(row.cost)}</b></span>
    <span>P/L <Pl value={row.profit} /></span>
  </div>
);

const DeleteModal = ({ target, onCancel, onDeleted }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const isTeam = target.kind === "team";
  const expected = isTeam ? target.name : target.email;
  const doDelete = async () => {
    setBusy(true);
    try {
      const url = isTeam ? `/admin/teams/${target.id}` : `/admin/users/${target.id}`;
      const { data } = await api.delete(url, { data: { confirm: text } });
      toast.success(isTeam ? `Team deleted (${data.members_deleted} member account(s) removed)` : `${data.email} deleted`);
      onDeleted();
    } catch (e) { toast.error(errDetail(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="delete-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-2 mb-3">
          <Warning size={22} className="text-red-600" />
          <h3 className="font-heading font-bold text-gray-900">
            Delete {isTeam ? `team "${target.name}"` : target.email}?
          </h3>
        </div>
        <div className="text-sm text-gray-600 mb-4 leading-relaxed">
          This permanently deletes {isTeam
            ? <>all <b>{target.seats_used} member account(s)</b>, their emails, memories, rules, contacts, API keys, calendar events and attachments — and <b>cancels the team's Stripe subscription</b>.</>
            : <>this account and all its emails, memories, rules, contacts, API keys, calendar events and attachments.</>}
          <span className="block mt-2 font-semibold text-red-600">This cannot be undone.</span>
        </div>
        <label className="text-xs font-medium text-gray-500 block mb-1">
          Type <span className="font-mono text-gray-800">{expected}</span> to confirm
        </label>
        <input data-testid="delete-confirm-input" value={text} onChange={(e) => setText(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-red-500"
          placeholder={expected} />
        <div className="flex justify-end gap-2">
          <button data-testid="delete-cancel-btn" onClick={onCancel}
            className="text-sm px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
          <button data-testid="delete-confirm-btn" onClick={doDelete}
            disabled={busy || text.trim().toLowerCase() !== expected.toLowerCase()}
            className="text-sm px-4 py-2 rounded-md bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-40">
            {busy ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserActions = ({ u, onUpdate, onToggleDisable, onDelete }) => {
  if (u.role === "superadmin") return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <button data-testid={`user-suspend-btn-${u.id}`}
        onClick={() => onUpdate(u, { status: u.status === "suspended" ? "active" : "suspended" })}
        className={`text-xs rounded-full px-2.5 py-1 font-semibold ${u.status === "suspended" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
        {u.status === "suspended" ? "Suspended" : "Active"}
      </button>
      <button data-testid={`user-disable-btn-${u.id}`} data-state={u.disabled ? "disabled" : "enabled"} onClick={() => onToggleDisable(u)}
        className={`text-xs font-semibold rounded px-2 py-1 flex items-center gap-1 ${u.disabled ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-red-50 text-red-700 hover:bg-red-100"}`}>
        <Prohibit size={12} /> {u.disabled ? "Enable" : "Disable"}
      </button>
      <button data-testid={`user-delete-btn-${u.id}`} onClick={() => onDelete(u)}
        className="text-xs font-semibold rounded px-2 py-1 bg-gray-100 text-gray-600 hover:bg-red-600 hover:text-white flex items-center gap-1">
        <Trash size={12} /> Delete
      </button>
    </div>
  );
};

export default function AdminUsers() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | teams | personal
  const [expanded, setExpanded] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/users-overview"); setData(data); }
    catch (e) { toast.error(errDetail(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateUser = async (u, patch) => {
    try { await api.patch(`/admin/users/${u.id}`, patch); toast.success("User updated"); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const toggleDisabled = async (u) => {
    const action = u.disabled ? "enable" : "disable";
    if (!window.confirm(`${action === "disable" ? "Disable" : "Enable"} ${u.email}?`)) return;
    try { await api.put(`/admin/users/${u.id}/${action}`); toast.success(`Account ${action}d`); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const toggleTeamDisabled = async (t) => {
    const action = t.disabled ? "enable" : "disable";
    if (!window.confirm(`${action === "disable" ? "Disable" : "Enable"} ALL members of "${t.name}"?`)) return;
    try {
      const { data } = await api.put(`/admin/teams/${t.id}/${action}`);
      toast.success(`Team ${action}d — ${data.members_affected} member(s) affected`); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const q = search.trim().toLowerCase();
  const matchUser = (u) => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);

  const teams = useMemo(() => {
    if (!data) return [];
    return data.teams.filter((t) => !q || t.name.toLowerCase().includes(q) || t.members.some(matchUser));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, q]);
  const personal = useMemo(() => (data ? data.personal.filter(matchUser) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, q]);

  const FilterPill = ({ id, label }) => (
    <button data-testid={`filter-${id}`} onClick={() => setFilter(id)}
      className={`text-xs font-semibold rounded-full px-3.5 py-1.5 transition-colors ${filter === id ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
      {label}
    </button>
  );

  return (
    <AdminShell title="Users & Teams" subtitle={`Every registered account · economics for ${data?.month || "this month"}`} testId="admin-users-page">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="users-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email or team…"
            className="w-full border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex items-center gap-1.5">
          <FilterPill id="all" label="All" /><FilterPill id="teams" label="Teams" /><FilterPill id="personal" label="Personal" />
        </div>
        <span data-testid="accounts-count" className="text-xs text-gray-400 ml-auto">{data?.total_accounts ?? "…"} accounts</span>
      </div>

      {(filter === "all" || filter === "teams") && (
        <div className="mb-7">
          <div className="text-[11px] font-semibold tracking-wider text-gray-400 mb-2">TEAMS ({teams.length})</div>
          <div className="space-y-2">
            {teams.length === 0 && <div className="text-sm text-gray-400 border border-dashed border-gray-200 rounded-md p-4">No teams{q ? " match your search" : " yet"}.</div>}
            {teams.map((t) => (
              <div key={t.id} data-testid={`team-card-${t.id}`} className={`border rounded-md ${t.disabled ? "border-red-200 bg-red-50/40" : "border-gray-200 bg-white"}`}>
                <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
                  <button data-testid={`team-expand-btn-${t.id}`}
                    onClick={() => setExpanded((e) => ({ ...e, [t.id]: !e[t.id] }))}
                    className="flex items-center gap-2 text-left flex-1 min-w-[200px]">
                    {expanded[t.id] ? <CaretDown size={14} className="text-gray-400" /> : <CaretRight size={14} className="text-gray-400" />}
                    <Buildings size={17} className="text-blue-600" />
                    <span className="font-semibold text-gray-900 text-sm truncate max-w-[260px]" title={t.name}>{t.name}</span>
                    {t.disabled && <span className="text-[10px] font-bold bg-red-100 text-red-700 rounded-full px-2 py-0.5">DISABLED</span>}
                    <span className="text-xs text-gray-400">{t.seats_used}/{t.seats} seats · {t.package_name || "no plan"}</span>
                    <CodeBadge row={t} testId={`team-code-badge-${t.id}`} />
                  </button>
                  <EconChips row={t} testId={`team-econ-${t.id}`} />
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`team-disable-btn-${t.id}`} data-state={t.disabled ? "disabled" : "enabled"} onClick={() => toggleTeamDisabled(t)}
                      className={`text-xs font-semibold rounded px-2 py-1 flex items-center gap-1 ${t.disabled ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-red-50 text-red-700 hover:bg-red-100"}`}>
                      {t.disabled ? <><CheckCircle size={12} /> Enable team</> : <><Prohibit size={12} /> Disable team</>}
                    </button>
                    <button data-testid={`team-delete-btn-${t.id}`} onClick={() => setDeleteTarget({ kind: "team", ...t })}
                      className="text-xs font-semibold rounded px-2 py-1 bg-gray-100 text-gray-600 hover:bg-red-600 hover:text-white flex items-center gap-1">
                      <Trash size={12} /> Delete team
                    </button>
                  </div>
                </div>
                {expanded[t.id] && (
                  <div className="border-t border-gray-100 divide-y divide-gray-50" data-testid={`team-members-${t.id}`}>
                    {t.members.map((m) => (
                      <div key={m.id} data-testid={`member-row-${m.id}`} className="flex items-center gap-3 pl-11 pr-4 py-2.5 flex-wrap">
                        {m.team_role === "team_admin" ? <Crown size={14} className="text-amber-500 shrink-0" /> : <UserIcon size={14} className="text-gray-300 shrink-0" />}
                        <div className="flex-1 min-w-[180px]">
                          <div className="text-sm text-gray-800">{m.name} {m.disabled && <span className="text-[10px] font-bold text-red-600">⊘ disabled</span>}</div>
                          <div className="text-xs text-gray-400">{m.email}{m.team_role === "team_admin" ? " · team admin (payer)" : ""}</div>
                        </div>
                        <span className="text-xs text-gray-500">{Number(m.tokens).toLocaleString()} tok · {cost4(m.cost)} · {m.email_count} emails</span>
                        <UserActions u={m} onUpdate={updateUser} onToggleDisable={toggleDisabled}
                          onDelete={(u) => setDeleteTarget({ kind: "user", ...u })} />
                      </div>
                    ))}
                    {t.members.length === 0 && <div className="pl-11 py-3 text-xs text-gray-400">No members yet.</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(filter === "all" || filter === "personal") && (
        <div>
          <div className="text-[11px] font-semibold tracking-wider text-gray-400 mb-2">PERSONAL USERS ({personal.length})</div>
          <div className="border border-gray-200 rounded-md overflow-x-auto bg-white">
            <table className="w-full text-sm min-w-[1080px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs text-gray-500">
                  <th className="px-4 py-2.5 font-medium">User</th>
                  <th className="px-3 py-2.5 font-medium">Plan</th>
                  <th className="px-3 py-2.5 font-medium">Revenue</th>
                  <th className="px-3 py-2.5 font-medium">Tokens</th>
                  <th className="px-3 py-2.5 font-medium">My cost</th>
                  <th className="px-3 py-2.5 font-medium">P/L</th>
                  <th className="px-3 py-2.5 font-medium">Emails</th>
                  <th className="px-3 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {personal.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-gray-100 last:border-0">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900 flex items-center gap-1.5">
                        {u.name}
                        {u.role === "superadmin" && <span className="text-[10px] bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 font-bold">superadmin</span>}
                        {u.disabled && <span className="text-[10px] bg-red-100 text-red-700 rounded-full px-2 py-0.5 font-bold">⊘ disabled</span>}
                      </div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      {u.role === "superadmin" ? <span className="text-xs text-gray-400">—</span> : (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <select data-testid={`user-plan-select-${u.id}`} value={u.plan} onChange={(e) => updateUser(u, { plan: e.target.value })}
                            className="border border-gray-200 rounded px-2 py-1 text-xs bg-white">
                            <option value="free">free</option><option value="pro">pro</option><option value="enterprise">enterprise</option>
                          </select>
                          <CodeBadge row={u} testId={`user-code-badge-${u.id}`} />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">{money(u.revenue)}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">{Number(u.tokens).toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">{cost4(u.cost)}</td>
                    <td className="px-3 py-2.5 text-xs"><Pl value={u.profit} /></td>
                    <td className="px-3 py-2.5 text-xs text-gray-500">{u.email_count}</td>
                    <td className="px-3 py-2.5">
                      <UserActions u={u} onUpdate={updateUser} onToggleDisable={toggleDisabled}
                        onDelete={(x) => setDeleteTarget({ kind: "user", ...x })} />
                    </td>
                  </tr>
                ))}
                {personal.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-400">No personal users{q ? " match your search" : ""}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <DeleteModal target={deleteTarget} onCancel={() => setDeleteTarget(null)}
        onDeleted={() => { setDeleteTarget(null); load(); }} />
    </AdminShell>
  );
}
